import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  forwardRef,
  inject,
  input,
  output,
} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
} from '@angular/material/autocomplete';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {MatTooltipModule} from '@angular/material/tooltip';
import {ProductGroupEntity} from '@em/data-feed/util-types';
import {
  EmAddNewItemCardComponent,
  EmFabButtonComponent,
  EmSelectModule,
} from '@em/shared/ui';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {EmProductGroupNamePipe} from '../../pipes/product-group-name/product-group-name.pipe';
import {EmProductGroupCardComponent} from '../product-group-card/product-group-card.component';

@Component({
  selector: 'em-select-product-group',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    MatTooltipModule,
    MatFormFieldModule,
    MatAutocompleteModule,
    MatInputModule,
    EmSelectModule,
    EmProductGroupNamePipe,
    EmFabButtonComponent,
    EmAddNewItemCardComponent,
    forwardRef(() => EmProductGroupCardComponent),
  ],
  templateUrl: './select-product-group.component.html',
  styleUrls: ['./select-product-group.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EmSelectProductGroupComponent {
  readonly translateService = inject(TranslateService);
  readonly cdr = inject(ChangeDetectorRef);

  readonly groups = input.required<(ProductGroupEntity[] | null) | undefined>();
  readonly selected = input<string | null>();
  readonly showPreview = input<boolean>(false);
  readonly showAddNew = input<boolean>(false);
  readonly required = input<boolean>(false);
  readonly label = input<string>();
  readonly allOption = input<string>();

  readonly groupChanged = output<ProductGroupEntity | null>();
  readonly groupEdited = output<ProductGroupEntity>();
  readonly addNewClick = output<void>();

  readonly selectedGroup = computed(() => {
    const selectedId = this.selected();
    if (selectedId) {
      return this.groups()?.find((g) => g.uuid === selectedId);
    }

    return null;
  });

  formControl = new FormControl<ProductGroupEntity | string | null>(null);

  constructor() {
    effect(() => {
      const isRequired = this.required();
      this.formControl.setValidators(isRequired ? [Validators.required] : []);
      this.formControl.updateValueAndValidity();
    });

    effect(() => {
      this.formControl.setValue(this.selectedGroup() || null);
    });
  }

  inputValue = toSignal(this.formControl.valueChanges);

  // Template Groups has names with translation keys.
  translatedGroups = computed(() => {
    const groups = this.groups();
    if (!groups?.length) {
      return [];
    }
    return groups.map((g) => ({
      ...g,
      name: this.translateService.instant(g.name),
    }));
  });

  sortedGroups = computed(() => {
    const groups = this.translatedGroups();
    if (!groups?.length) {
      return [];
    }
    return [...groups].sort((a, b) => a?.name.localeCompare(b?.name));
  });

  filteredGroups = computed(() => {
    const groups = this.sortedGroups();
    const inputValue = this.inputValue();

    if (!groups?.length) {
      return [];
    }

    if (!inputValue || typeof inputValue === 'object') {
      return groups;
    }

    return groups.filter((g) =>
      g?.name.toLowerCase().includes((inputValue as string).toLowerCase()),
    );
  });

  onOptionSelected(event: MatAutocompleteSelectedEvent): void {
    this.groupChanged.emit(event.option.value);
  }

  onInputFocus(event: Event) {
    const inputElement = event.target as HTMLInputElement;
    inputElement.select();
  }

  onInputBlur() {
    setTimeout(() => {
      console.log(this.inputValue());
      const inputValue = this.inputValue();
      if (typeof inputValue === 'object') {
        return;
      } else {
        const group = this.groups()?.find((g) => g.name === inputValue);
        if (group) {
          this.groupChanged.emit(group);
        } else {
          this.formControl.setValue(null);
          this.groupChanged.emit(null);
        }
      }
    }, 100);
  }

  displayFn(group: ProductGroupEntity | string): string {
    if (typeof group === 'string') {
      return group;
    }
    return group ? group.name || '' : '';
  }
}
